import { useSelector } from 'react-redux';
import omitBy from 'lodash/omitBy';

import { Capability, FeatureFlag } from '__types__';
import { selectActiveCapabilities } from 'store/userGroups';
import { hasFlag, checkOverallAccess } from './accessCheckers';
import { useScopedFeatureFlagNames } from './useScopedFeatureFlagNames';

type MenuItem = {
  flag: FeatureFlag | null;
  capabilities: Capability | Capability[] | null;
  capabilitiesAtLeastOne: Capability[] | null;
  default_route?: string;
  route?: string;
  children?: MenuItem[];
};

/**
 * Filter an array of menu items to only items which the user has permissions to.
 */
export const filterMenuAccess = (
  menuItems: MenuItem[],
  activeFeatureFlags: FeatureFlag[],
  activeCapabilities: Partial<Record<Capability, boolean>>
) => {
  return menuItems.reduce<MenuItem[]>((acc, menuItem) => {
    if (!menuItem.flag && !menuItem.capabilities) {
      acc.push(menuItem);
      return acc;
    }

    const isGranted = checkOverallAccess(
      menuItem.flag,
      menuItem.capabilities,
      menuItem.capabilitiesAtLeastOne,
      activeFeatureFlags,
      activeCapabilities
    );

    if (isGranted) {
      // check if has permission to access default route, if not fallback to route
      if (
        menuItem.default_route &&
        !hasFlag(
          activeFeatureFlags,
          menuItem.default_route.split('/')[1] as FeatureFlag
        )
      ) {
        menuItem.default_route = menuItem.route;
      }

      // recursively check childrens feature flag permissions
      if (menuItem.children) {
        menuItem.children = filterMenuAccess(
          menuItem.children,
          activeFeatureFlags,
          activeCapabilities
        );
      }
      acc.push(menuItem);
    }
    return acc;
  }, []);
};

/**
 * Filter an object of menu items to only items which the user has permissions to.
 */
const filterObjectMenuAccess = (
  menuItems: Record<string, MenuItem>,
  activeFeatureFlags: FeatureFlag[],
  activeCapabilities: Partial<Record<Capability, boolean>>
) => {
  return omitBy(menuItems, menuItem => {
    return !checkOverallAccess(
      menuItem.flag,
      menuItem.capabilities,
      null,
      activeFeatureFlags,
      activeCapabilities
    );
  });
};

/**
 * Filter a set of menu items to only items which the user has permissions to.
 * Uses browsing AppScope feature flags (pending → empty / deny).
 */
const useFilterMenuAccess = (
  menuItems: MenuItem[] | Record<string, MenuItem>
) => {
  const activeFeatureFlags = useScopedFeatureFlagNames();
  const activeCapabilities = useSelector(selectActiveCapabilities);

  // Pending flags → deny. Mirror the caller's shape (array vs record) so
  // `.map` / key iteration keep working without a type flip.
  if (activeFeatureFlags == null) {
    return Array.isArray(menuItems) ? [] : {};
  }

  if (Array.isArray(menuItems)) {
    return filterMenuAccess(menuItems, activeFeatureFlags, activeCapabilities);
  }

  return filterObjectMenuAccess(
    menuItems,
    activeFeatureFlags,
    activeCapabilities
  );
};

export default useFilterMenuAccess;
